// Adapted from beautifului.dev (MIT, © 2026 Shane Levine).
import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * The "Drive" wavefront: a 3x3 grid where each cell lights on its own delay,
 * so a diagonal front sweeps left to right. The 650ms cycle is shorter than
 * the sweep, so two fronts are always in flight and the loader never looks
 * like it stalled.
 */
const CELL_DELAYS_MS = Array.from({ length: 9 }, (_, index) => {
  const row = Math.floor(index / 3);
  const column = index % 3;
  return (column + Math.abs(row - 1)) * 90;
});

/** The one cell left lit when the animation is off (reduced motion). */
const CENTER_CELL_INDEX = 4;

/**
 * `2.4s` under a minute, `1m 03s` over it. Tenths are resolved first so the
 * two branches can never disagree (no `60.0s`, no `1m 60s`).
 */
export const formatElapsed = (ms: number): string => {
  const tenths = Math.round(Math.max(0, ms) / 100);
  if (tenths < 600) return `${(tenths / 10).toFixed(1)}s`;

  const seconds = Math.floor(tenths / 10);
  return `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, '0')}s`;
};

/**
 * Milliseconds since `startedAt`, ticking every 100ms while `running`. Once
 * the turn ends the value freezes at the last reading, which is what the
 * collapsed summary shows for a turn whose `turn.ms` never arrived.
 */
export const useElapsedMs = (
  startedAt: number | null | undefined,
  running: boolean
): number => {
  const [elapsed, setElapsed] = useState(() =>
    startedAt == null ? 0 : Math.max(0, Date.now() - startedAt)
  );

  useEffect(() => {
    if (!running || startedAt == null) return;

    const read = () => setElapsed(Math.max(0, Date.now() - startedAt));

    read();
    const interval = setInterval(read, 100);
    return () => clearInterval(interval);
  }, [running, startedAt]);

  return elapsed;
};

interface ChatThinkingIndicatorProps {
  /** What the runtime says it is doing right now. */
  label?: string;
  /** When the turn started, for the elapsed readout. */
  startedAt?: number;
  className?: string;
}

/**
 * The live loader for a running turn: the pixel grid, the shimmering label,
 * and an elapsed timer. Three signals that the turn is moving, none of which
 * needs the student to read anything (ORA-597).
 */
const ChatThinkingIndicator = ({
  label,
  startedAt,
  className,
}: ChatThinkingIndicatorProps) => {
  const elapsed = useElapsedMs(startedAt, true);

  return (
    <div
      className={cn('chat-motion flex items-center gap-2.5', className)}
      role="status"
      aria-live="polite"
    >
      <span
        aria-hidden="true"
        className="grid shrink-0 grid-cols-[repeat(3,4px)] gap-[1.5px]"
      >
        {CELL_DELAYS_MS.map((delay, index) => (
          <span
            key={index}
            className={cn(
              'animate-chat-pixel-on size-[4px] rounded-[1px] bg-fg-primary opacity-15',
              // Animation off: one lit cell reads as a loader, nine dim ones
              // read as a rendering bug.
              index === CENTER_CELL_INDEX && 'motion-reduce:opacity-100'
            )}
            style={{ animationDelay: `${delay}ms` }}
          />
        ))}
      </span>

      <span
        className="animate-chat-shimmer-text min-w-0 bg-clip-text text-sm font-medium text-transparent motion-reduce:bg-none motion-reduce:text-fg-secondary"
        style={{
          backgroundImage:
            'linear-gradient(90deg, var(--text-secondary) 35%, var(--text-primary) 50%, var(--text-secondary) 65%)',
          backgroundSize: '200% 100%',
        }}
      >
        {label ?? 'Thinking'}
      </span>

      {startedAt == null ? null : (
        <span className="shrink-0 font-mono text-xs text-fg-secondary tabular-nums">
          {formatElapsed(elapsed)}
        </span>
      )}
    </div>
  );
};

export default ChatThinkingIndicator;
